let _config = null;
/**
 * Initialize the API client. Must be called once at app startup.
 *
 * On web: `{ baseUrl: window.location.origin }` (uses cookies)
 * On mobile: `{ baseUrl: 'https://work.alturex.com', getToken: () => SecureStore.getItemAsync('token') }`
 *            or the legacy tickets host during the compatibility window.
 */
export function configureApi(config) {
    _config = config;
}
export function getApiConfig() {
    if (!_config) {
        throw new Error('API client not configured. Call configureApi() first.');
    }
    return _config;
}
const REQUEST_TIMEOUT_MS = 30_000;
function isAbortError(err) {
    return (typeof err === 'object' &&
        err !== null &&
        err.name === 'AbortError');
}
/**
 * Extract the most useful user-facing message from an API failure.
 *
 * The request client throws ApiError-shaped plain objects rather than Error
 * instances. Transition validation failures also carry their actionable
 * reasons one level deeper in `data.validation.errors`.
 */
export function getApiErrorMessage(err, fallback = 'Request failed') {
    const apiError = err;
    const validationErrors = apiError?.data?.validation?.errors;
    if (Array.isArray(validationErrors)) {
        const messages = validationErrors.filter((value) => typeof value === 'string' && value.trim().length > 0);
        if (messages.length === 1)
            return messages[0];
        if (messages.length > 1) {
            return `${messages.length} validation errors: ${messages.join('; ')}`;
        }
    }
    const details = apiError?.data?.details;
    if (Array.isArray(details)) {
        const messages = details
            .map((detail) => detail && typeof detail === 'object'
            ? detail.message
            : undefined)
            .filter((value) => typeof value === 'string' && value.trim().length > 0);
        if (messages.length === 1)
            return messages[0];
        if (messages.length > 1)
            return messages.join('; ');
    }
    const candidates = [
        apiError?.data?.message,
        apiError?.message,
        apiError?.data?.error,
        apiError?.error,
        err instanceof Error ? err.message : undefined,
    ];
    return (candidates.find((value) => typeof value === 'string' && value.trim().length > 0) ?? fallback);
}
/**
 * The server could not confirm whether a ticket save or status change
 * committed: its database response was lost and the read-back failed too
 * (503 `save_outcome_unknown`). Not a gateway bounce: the request reached the
 * app. Clients reload the ticket and reconcile, the way they handle a 409,
 * and never resend the same payload blindly.
 */
export function isSaveOutcomeUnknown(err) {
    if (!err || typeof err !== 'object')
        return false;
    const apiError = err;
    return apiError.status === 503 && apiError.data?.error === 'save_outcome_unknown';
}
/**
 * The save committed, but the server could not read the ticket back to answer
 * with it (500 `saved_reload_required`). Retrying would apply it again; the
 * client reloads instead.
 */
export function isSavedReloadRequired(err) {
    if (!err || typeof err !== 'object')
        return false;
    const apiError = err;
    return apiError.status === 500 && apiError.data?.error === 'saved_reload_required';
}
/**
 * The write committed even though the answer is an error: 500
 * saved_reload_required, or any error body whose partial_save says the ticket
 * row was written. A queued replay that gets this is done, not failed.
 */
export function isCommittedSaveError(err) {
    if (!err || typeof err !== 'object')
        return false;
    if (isSavedReloadRequired(err))
        return true;
    const data = err.data;
    return data?.partial_save?.parent_persisted === true;
}
/**
 * A ticket save or status change whose answer means "reload the ticket, do
 * not resend": a version conflict (409), an outcome the server could not
 * confirm (503 save_outcome_unknown), or a save that committed but could not
 * be answered with the ticket (500 saved_reload_required, or any error body
 * whose partial_save says the ticket row was written). The client's copy may
 * be missing ids the server assigned, so resending it could add new lines a
 * second time.
 */
export function requiresTicketReload(err) {
    return ticketRecoveryReason(err) !== null;
}
/**
 * The recovery reason for a ticket save or status change, or null for an
 * ordinary answer. 'uncertain': it may have committed (503
 * save_outcome_unknown). 'committed': it did, without the ticket in the
 * answer. 'conflict': 409 ticket_version_conflict. Each client's recovery
 * state machine enters on any of them, from any write path.
 */
export function ticketRecoveryReason(err) {
    if (!err || typeof err !== 'object')
        return null;
    if (isSaveOutcomeUnknown(err))
        return 'uncertain';
    if (isCommittedSaveError(err))
        return 'committed';
    const apiError = err;
    return apiError.status === 409 && apiError.data?.error === 'ticket_version_conflict'
        ? 'conflict'
        : null;
}
/**
 * Whether an offline mutation should be attempted again automatically.
 * Validation, auth, permission, conflict, and not-found responses require a
 * user or data change; retrying them cannot make progress. Request timeouts,
 * rate limits, and server availability failures can be transient.
 */
export function isRetryableApiError(err) {
    const status = err && typeof err === 'object'
        ? err.status
        : undefined;
    if (typeof status !== 'number')
        return true;
    // Needs a reload and a decision, like a conflict; a blind retry could
    // apply the same edit on top of itself.
    if (requiresTicketReload(err))
        return false;
    if (status === 0 || status === 408 || status === 429)
        return true;
    return status < 400 || status >= 500;
}
/**
 * Core request function shared by all API methods.
 * Supports both cookie auth (web) and Bearer token auth (mobile).
 */
export async function request(endpoint, options = {}) {
    const config = getApiConfig();
    const url = `${config.baseUrl}/api${endpoint}`;
    const headers = {
        ...options.headers,
    };
    const isFormData = options.body instanceof FormData;
    // Set Content-Type for JSON bodies (skip for FormData)
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    // Add Bearer token for mobile auth
    let requestToken = null;
    if (config.getToken) {
        requestToken = await config.getToken();
        if (requestToken) {
            headers['Authorization'] = `Bearer ${requestToken}`;
        }
    }
    const requestCredential = {
        token: requestToken,
        generation: config.getAuthGeneration?.(),
    };
    // Set up request timeout, optionally linked to a caller-owned abort signal.
    const timeoutController = new AbortController();
    let didTimeout = false;
    const timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS;
    const timeoutId = setTimeout(() => {
        didTimeout = true;
        timeoutController.abort();
    }, timeoutMs);
    let requestSignal = timeoutController.signal;
    let combinedController;
    let timeoutAbortHandler;
    let externalAbortHandler;
    if (options.signal) {
        combinedController = new AbortController();
        const abortCombined = () => {
            if (!combinedController?.signal.aborted)
                combinedController?.abort();
        };
        timeoutAbortHandler = abortCombined;
        externalAbortHandler = abortCombined;
        if (timeoutController.signal.aborted || options.signal.aborted) {
            abortCombined();
        }
        else {
            timeoutController.signal.addEventListener('abort', timeoutAbortHandler, { once: true });
            options.signal.addEventListener('abort', externalAbortHandler, { once: true });
        }
        requestSignal = combinedController.signal;
    }
    try {
        const fetchOptions = {
            method: options.method,
            headers,
            // Include cookies for web auth, omit for mobile (Bearer token)
            credentials: config.getToken ? 'omit' : 'include',
            signal: requestSignal,
        };
        if (options.cache)
            fetchOptions.cache = options.cache;
        // Set body - stringify JSON, pass FormData directly
        if (options.body !== undefined) {
            fetchOptions.body = isFormData
                ? options.body
                : JSON.stringify(options.body);
        }
        // The caller's last check, with no await between it and the request.
        options.beforeSend?.(headers);
        const response = await fetch(url, fetchOptions);
        // Check response status BEFORE parsing body
        if (!response.ok) {
            if (response.status === 401) {
                // Mobile closes offline access as soon as the response status is known.
                // A delayed response from a superseded token must not revoke the newer
                // session. Keep the current-credential check and boundary synchronous:
                // awaiting cleanup can deadlock a queue replay that cleanup must drain.
                if (!config.isCredentialCurrent || config.isCredentialCurrent(requestCredential)) {
                    config.onUnauthorized?.();
                }
            }
            let data = null;
            let message = `Request failed with status ${response.status}`;
            try {
                data = await response.json();
                message =
                    data?.message ||
                        data?.error ||
                        message;
            }
            catch {
                // Response is not JSON (e.g., 502 from nginx, plain-text 500)
                try {
                    const text = await response.text();
                    if (text) {
                        message = text.slice(0, 200);
                    }
                }
                catch {
                    // Body already consumed or unreadable, keep default message
                }
            }
            const error = {
                status: response.status,
                message,
                data,
            };
            throw error;
        }
        // Parse successful response
        const data = await response.json();
        return data;
    }
    catch (err) {
        // Convert only our timeout abort to a retryable status-0 error. Caller-owned
        // aborts are cancellations and must remain AbortError.
        if (didTimeout && isAbortError(err)) {
            const error = {
                status: 0,
                message: `Request timed out after ${timeoutMs / 1000}s`,
                data: null,
            };
            throw error;
        }
        throw err;
    }
    finally {
        clearTimeout(timeoutId);
        if (options.signal && externalAbortHandler) {
            options.signal.removeEventListener('abort', externalAbortHandler);
        }
        if (timeoutAbortHandler) {
            timeoutController.signal.removeEventListener('abort', timeoutAbortHandler);
        }
    }
}
//# sourceMappingURL=client.js.map